import { useState } from "react";
import { Box, Button } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useTranslate } from "@refinedev/core";

import { DataGrid } from "@shared/components/DataGrid";
import { TableCell } from "@shared/components";
import { usePaginatedTable } from "@shared/hooks";
import { PartnerMonthly } from "@modules/partners/types";
import { formatCurrency } from "@core/utils/masks";

import { GenerateMonthlyDialog } from "./GenerateMonthlyDialog";

export function MonthlyTab() {
  const translate = useTranslate();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<PartnerMonthly>({
    resource: "v1/partner_monthly_admin",
    sorters: { ordering: "-year" },
  });

  const columns: GridColDef[] = [
    {
      field: "year",
      headerName: translate("v1/partner_monthly_admin.fields.year"),
      minWidth: 100,
      renderCell: ({ value }) => <TableCell.Text value={value} />,
    },
    {
      field: "month",
      headerName: translate("v1/partner_monthly_admin.fields.month"),
      minWidth: 100,
      renderCell: ({ value }) => <TableCell.Text value={value} />,
    },
    {
      field: "total_mrr",
      headerName: translate("v1/partner_monthly_admin.fields.total_mrr"),
      minWidth: 140,
      renderCell: ({ value }) => (
        <TableCell.Text value={formatCurrency(value)} />
      ),
    },
    {
      field: "total_sales",
      headerName: translate("v1/partner_monthly_admin.fields.total_sales"),
      minWidth: 140,
      renderCell: ({ value }) => (
        <TableCell.Text value={formatCurrency(value)} />
      ),
    },
    {
      field: "total_commission",
      headerName: translate("v1/partner_monthly_admin.fields.total_commission"),
      minWidth: 160,
      renderCell: ({ value }) => (
        <TableCell.Text value={formatCurrency(value)} />
      ),
    },
    {
      field: "referrals_count",
      headerName: translate("v1/partner_monthly_admin.fields.referrals_count"),
      minWidth: 140,
      renderCell: ({ value }) => <TableCell.Text value={value} />,
    },
    {
      field: "partners_count",
      headerName: translate("v1/partner_monthly_admin.fields.partners_count"),
      minWidth: 140,
      renderCell: ({ value }) => <TableCell.Text value={value} />,
    },
    {
      field: "active_agreements_count",
      headerName: translate("v1/partner_monthly_admin.fields.active_agreements_count"),
      minWidth: 160,
      renderCell: ({ value }) => <TableCell.Text value={value} />,
    },
  ];

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          {translate("partners.actions.generate")}
        </Button>
      </Box>

      <DataGrid
        {...tableProps}
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={(row) => `${row.year}-${row.month}`}
      />

      <GenerateMonthlyDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          refetch();
        }}
      />
    </>
  );
}
